import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import CheckIcon from '@mui/icons-material/Check';
import MapIcon from '@mui/icons-material/Map';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import mapboxgl from 'mapbox-gl';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

const SAMPLE_PINS: [number, number][] = [
  [-43.1729, -22.9068], [-46.6333, -23.5505], [2.3522, 48.8566],
  [-3.7038, 40.4168], [12.4964, 41.9028], [23.7275, 37.9838],
  [139.6917, 35.6895], [-73.9857, 40.7484], [-118.2437, 34.0522],
  [151.2093, -33.8688], [28.9784, 41.0082], [18.4241, -33.9249],
  [100.5018, 13.7563], [116.4074, 39.9042], [-99.1332, 19.4326],
];

const featureKeys = [
  { icon: MapIcon, titleKey: 'landing.featureMap', descKey: 'landing.featureMapDesc' },
  { icon: PhotoCameraIcon, titleKey: 'landing.featureAlbum', descKey: 'landing.featureAlbumDesc' },
  { icon: BarChartIcon, titleKey: 'landing.featureStats', descKey: 'landing.featureStatsDesc' },
  { icon: PeopleIcon, titleKey: 'landing.featureCommunity', descKey: 'landing.featureCommunityDesc' },
];

const stepKeys = [
  { number: 1, titleKey: 'landing.step1Title', descKey: 'landing.step1Desc' },
  { number: 2, titleKey: 'landing.step2Title', descKey: 'landing.step2Desc' },
  { number: 3, titleKey: 'landing.step3Title', descKey: 'landing.step3Desc' },
];

const planKeys = [
  { name: 'Free', price: 'R$0', photos: '100', highlighted: false },
  { name: 'Starter', price: 'R$19,90', photos: '1.000', highlighted: false },
  { name: 'Explorer', price: 'R$49,90', photos: '10.000', highlighted: true },
  { name: 'Premium', price: 'R$99,90', photos: '100.000', highlighted: false },
];

export function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [-43.1729, -22.9068],
      zoom: 2,
      projection: 'globe',
      interactive: true,
      attributionControl: false,
    });

    mapRef.current = map;

    map.on('style.load', () => {
      map.setFog({
        color: 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6,
      });
      SAMPLE_PINS.forEach(([lng, lat]) => {
        new mapboxgl.Marker({ color: '#d32f2f', scale: 0.8 })
          .setLngLat([lng, lat])
          .addTo(map);
      });
    });

    let userInteracting = false;
    let spinEnabled = true;

    function spinGlobe() {
      if (spinEnabled && !userInteracting && map.getZoom() < 5) {
        const center = map.getCenter();
        center.lng -= 2;
        map.easeTo({ center, duration: 1000, easing: (n: number) => n });
      }
    }

    map.on('mousedown', () => { userInteracting = true; });
    map.on('mouseup', () => { userInteracting = false; spinGlobe(); });
    map.on('dragend', () => { userInteracting = false; spinGlobe(); });
    map.on('touchstart', () => { userInteracting = true; });
    map.on('touchend', () => { userInteracting = false; spinGlobe(); });
    map.on('moveend', () => { spinGlobe(); });
    spinGlobe();

    return () => {
      spinEnabled = false;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Language Switcher */}
      <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '50%', backdropFilter: 'blur(4px)' }}>
        <LanguageSwitcher />
      </Box>

      {/* Hero */}
      <Box sx={{ position: 'relative', minHeight: '110vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box ref={mapContainerRef} sx={{ position: 'absolute', inset: 0, opacity: 0.85 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.15) 100%)', zIndex: 1 }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 12 } }}>
          <Box sx={{ maxWidth: 700 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <svg viewBox="0 0 60 72" width="64" height="77" xmlns="http://www.w3.org/2000/svg">
                <defs><linearGradient id="heroLogo" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#42a5f5"/><stop offset="100%" stopColor="#0d47a1"/></linearGradient></defs>
                <path d="M30 68 C30 68 56 44 56 28 C56 13.6 44.4 2 30 2 C15.6 2 4 13.6 4 28 C4 44 30 68 30 68Z" fill="url(#heroLogo)"/>
                <circle cx="30" cy="26" r="14" fill="white"/>
                <circle cx="30" cy="26" r="13" fill="none" stroke="#1565c0" strokeWidth="1.4"/>
                <ellipse cx="30" cy="26" rx="5.5" ry="13" fill="none" stroke="#1565c0" strokeWidth="1"/>
                <line x1="17" y1="26" x2="43" y2="26" stroke="#1565c0" strokeWidth="1"/>
              </svg>
              <Typography variant="h2" component="h1" sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '2.5rem', md: '4rem' }, lineHeight: 1.2, textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}>
                Globe Pinned
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' }, mb: 5, lineHeight: 1.6, textShadow: '1px 1px 4px rgba(0,0,0,0.6)' }}>
              {t('landing.heroSubtitle')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="contained" size="large" onClick={() => navigate('/auth/register')} sx={{ bgcolor: '#e53935', color: '#fff', px: 4, py: 1.5, fontSize: '1.1rem', '&:hover': { bgcolor: '#b71c1c' }, boxShadow: '0 4px 14px rgba(229,57,53,0.3)' }}>
                {t('landing.createAccount')}
              </Button>
              <Button variant="outlined" size="large" onClick={() => navigate('/auth/login')} sx={{ borderColor: 'rgba(255,255,255,0.8)', color: '#fff', px: 4, py: 1.5, fontSize: '1.1rem', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                {t('landing.login')}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" sx={{ mb: 2, fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            {t('landing.whyTitle')}
          </Typography>
          <Typography textAlign="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            {t('landing.whySubtitle')}
          </Typography>
          <Grid container spacing={4}>
            {featureKeys.map((feature) => (
              <Grid item xs={12} sm={6} md={3} key={t(feature.titleKey)}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <feature.icon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{t(feature.titleKey)}</Typography>
                    <Typography variant="body2" color="text.secondary">{t(feature.descKey)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Photos Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 2 }}>
                {t('landing.photosTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                {t('landing.photosDesc1')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                {t('landing.photosDesc2')}
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body1">{t('landing.photosBenefit1')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body1">{t('landing.photosBenefit2')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body1">{t('landing.photosBenefit3')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body1">{t('landing.photosBenefit4')}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Photo collage - scattered photos becoming organized */}
              <Box sx={{ position: 'relative', height: { xs: 300, md: 400 }, perspective: '1000px' }}>
                {/* Scattered photos (left/background) - real travel images */}
                <Box sx={{ position: 'absolute', top: 20, left: 0, width: 120, height: 90, borderRadius: 1, transform: 'rotate(-12deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=240&h=180&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 60, left: 40, width: 100, height: 75, borderRadius: 1, transform: 'rotate(8deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=200&h=150&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 10, left: 100, width: 90, height: 70, borderRadius: 1, transform: 'rotate(-5deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=180&h=140&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 120, left: 10, width: 110, height: 80, borderRadius: 1, transform: 'rotate(15deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=220&h=160&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 170, left: 80, width: 95, height: 70, borderRadius: 1, transform: 'rotate(-8deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=190&h=140&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 210, left: 20, width: 105, height: 75, borderRadius: 1, transform: 'rotate(11deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=210&h=150&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 250, left: 100, width: 90, height: 65, borderRadius: 1, transform: 'rotate(-14deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=180&h=130&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 290, left: 30, width: 100, height: 72, borderRadius: 1, transform: 'rotate(6deg)', boxShadow: 3, overflow: 'hidden', border: '3px solid #fff' }}>
                  <img src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=200&h=144&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>

                {/* Arrow from chaos to order */}
                <Box sx={{ position: 'absolute', top: '45%', left: '42%', transform: 'translate(-50%, -50%)', fontSize: '2rem', color: 'primary.main', opacity: 0.6 }}>
                  {String.fromCodePoint(0x27A1)}
                </Box>

                {/* Organized grid (right) */}
                {/* Organized grid */}
                <Box sx={{ position: 'absolute', top: 10, right: 0, width: 220, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.8, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, boxShadow: 4 }}>
                  <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 1, overflow: 'hidden' }}><img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=120&h=120&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>
                  <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 1, overflow: 'hidden' }}><img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=120&h=120&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>
                  <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 1, overflow: 'hidden' }}><img src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=120&h=120&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>
                  <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 1, overflow: 'hidden' }}><img src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=120&h=120&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>
                  <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 1, overflow: 'hidden' }}><img src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=120&h=120&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>
                  <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 1, overflow: 'hidden' }}><img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=120&h=120&fit=crop" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>
                </Box>
                {/* Label */}
                <Typography variant="caption" sx={{ position: 'absolute', bottom: 10, left: 30, color: 'text.secondary', fontStyle: 'italic' }}>{t('landing.disorganized') || 'Desorganizadas...'}</Typography>
                <Typography variant="caption" sx={{ position: 'absolute', bottom: 10, right: 30, color: 'primary.main', fontWeight: 600 }}>{t('landing.organized') || 'Organizadas!'}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How it works */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="md">
          <Typography variant="h3" textAlign="center" sx={{ mb: 6, fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>{t('landing.howItWorks')}</Typography>
          <Grid container spacing={4}>
            {stepKeys.map((step) => (
              <Grid item xs={12} md={4} key={step.number}>
                <Box textAlign="center">
                  <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700, mx: 'auto', mb: 2 }}>
                    {step.number}
                  </Box>
                  <Typography variant="h6" mb={1}>{t(step.titleKey)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t(step.descKey)}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" sx={{ mb: 2, fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>{t('landing.plans')}</Typography>
          <Typography textAlign="center" sx={{ mb: 8, color: 'text.secondary' }}>
            {t('landing.plansSubtitle')}
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {planKeys.map((plan) => (
              <Grid item xs={12} sm={6} md={3} key={plan.name}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'visible', border: plan.highlighted ? '2px solid' : '1px solid', borderColor: plan.highlighted ? 'primary.main' : 'grey.200', transform: plan.highlighted ? 'scale(1.05)' : 'none' }}>
                  {plan.highlighted && (
                    <Chip label={t("plans.popular")} size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', bgcolor: 'primary.main', color: '#fff', fontWeight: 700 }} />
                  )}
                  <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{plan.name}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>{plan.price}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}></Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1} sx={{ flex: 1, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="primary" fontSize="small" />
                        <Typography variant="body2">{plan.photos} {t('landing.photosLimit')}</Typography>
                      </Box>
                    </Stack>
                    <Button variant={plan.highlighted ? 'contained' : 'outlined'} fullWidth onClick={() => navigate('/auth/register')} sx={{ mt: 3 }}>
                      {t('common.start')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ py: 6, bgcolor: 'grey.900', color: 'grey.300' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" color="white" sx={{ fontWeight: 700, mb: 1 }}>{t('landing.heroTitle')}</Typography>
              <Typography variant="body2">{t('landing.footerDesc') || 'A melhor forma de registrar suas viagens e explorar o mundo.'}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" color="white" sx={{ mb: 1 }}>{t('landing.product')}</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>Recursos</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>{t('landing.plans')}</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>FAQ</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" color="white" sx={{ mb: 1 }}>{t('landing.company')}</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>{t('landing.about')}</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>Blog</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>{t('landing.contact')}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="white" sx={{ mb: 1 }}>Legal</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>{t('landing.terms')}</Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer', mb: 0.5 }}>{t('landing.privacy')}</Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
          <Typography variant="body2" textAlign="center" color="grey.500">
            {'\u00A9'} {new Date().getFullYear()} Globe Pinned. {t('landing.allRights')}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
